<script setup lang="ts">
/**
 * Which of the 88 shipped payment-method logos show in the landing footer.
 *
 * ── Every entry starts disabled, on purpose ──────────────────────────────────
 *
 * `DEFAULT_PAYMENT_METHODS` ships all 88 with `enabled: false` — the game may
 * not actually support half of these providers yet, and a logo in the footer
 * is an implicit claim that it does. This pane is the only way to turn one on;
 * there is no bulk "enable everything" here even though the API has a
 * `bulk-toggle` route, deliberately — an operator should look at each one.
 *
 * ── Grouping mirrors the backend's own comments ──────────────────────────────
 *
 * `payment-methods.constants.ts` groups the 88 by `sortOrder`, in blocks with
 * gaps between them (Cards end at 12, Digital wallets start at 20, and so on),
 * labelled only in a source comment. `GROUPS` below restates those same
 * boundaries so the operator sees the same shape the seed data was written in,
 * without the backend needing to expose group labels itself.
 */
import { computed } from 'vue';

import { adminApi, type PaymentMethodRow } from './admin-api';
import { usePane } from './pane';

const methods = usePane(() => adminApi.paymentMethods());

/** Lower bound of each group's `sortOrder`, ascending — see the file header. */
const GROUPS: ReadonlyArray<{ label: string; from: number }> = [
  { label: 'Cards', from: 1 },
  { label: 'Digital wallets', from: 20 },
  { label: 'Buy now pay later', from: 35 },
  { label: 'Bank / redirect', from: 55 },
  { label: 'E-wallets', from: 90 },
  { label: 'Nordic / regional wallets', from: 105 },
  { label: 'Regional banks', from: 120 },
  { label: 'Payment processors / platforms', from: 140 },
  { label: 'Security badges', from: 170 },
];

function groupLabelFor(sortOrder: number): string {
  let label = GROUPS[0].label;
  for (const g of GROUPS) {
    if (sortOrder < g.from) break;
    label = g.label;
  }
  return label;
}

/** Rows bucketed into their group, in `GROUPS` order, empty groups dropped. */
const grouped = computed(() => {
  const byLabel = new Map<string, PaymentMethodRow[]>();
  for (const row of methods.data.value ?? []) {
    const label = groupLabelFor(row.sortOrder);
    const bucket = byLabel.get(label);
    if (bucket) bucket.push(row);
    else byLabel.set(label, [row]);
  }
  return GROUPS.map((g) => ({ label: g.label, rows: byLabel.get(g.label) ?? [] })).filter(
    (g) => g.rows.length > 0,
  );
});

const enabledCount = computed(() => (methods.data.value ?? []).filter((m) => m.enabled).length);
const totalCount = computed(() => (methods.data.value ?? []).length);

function toggle(row: PaymentMethodRow): void {
  void methods.act(() => adminApi.togglePaymentMethod(row.slug, !row.enabled));
}
</script>

<template>
  <h1>Payment methods</h1>
  <p class="lede">
    Which logos show in the landing page footer. A toggle here is live
    immediately — no deploy needed — so only switch on what the game actually
    supports.
  </p>

  <p v-if="methods.error.value" class="bad">{{ methods.error.value }}</p>
  <p v-if="methods.loading.value && !methods.data.value" class="busy">Loading…</p>

  <p v-if="methods.data.value" class="note" data-test="admin-payment-methods-count">
    {{ enabledCount }} of {{ totalCount }} enabled.
  </p>

  <section v-for="group in grouped" :key="group.label">
    <h2>{{ group.label }}</h2>
    <ul class="payment-methods-list">
      <li v-for="row in group.rows" :key="row.slug" class="payment-method-row">
        <img
          :src="row.iconUrl"
          :alt="row.name"
          class="payment-method-icon"
          loading="lazy"
        />
        <span class="payment-method-name">{{ row.name }}</span>
        <label class="payment-method-toggle">
          <input
            type="checkbox"
            :checked="row.enabled"
            :disabled="methods.loading.value"
            :data-test="`admin-payment-method-${row.slug}`"
            @change="toggle(row)"
          />
          <span>{{ row.enabled ? 'Enabled' : 'Disabled' }}</span>
        </label>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.payment-methods-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(14rem, 1fr));
  gap: 0.4rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.payment-method-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.55rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.2rem;
  background: var(--steel-800);
}

.payment-method-icon {
  width: 1.6rem;
  height: 1.6rem;
  object-fit: contain;
  flex-shrink: 0;
  /* Several icons are dark marks meant for a light background. */
  background: #fff;
  border-radius: 0.15rem;
  padding: 0.1rem;
}

.payment-method-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: var(--steel-200);
  font-size: 0.8rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.payment-method-toggle {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-shrink: 0;
  color: var(--steel-400);
  font-family: var(--font-mono);
  font-size: 0.6rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
}

.payment-method-toggle input {
  width: 1rem;
  height: 1rem;
  min-height: 0;
  cursor: pointer;
}
</style>
